import { motion } from "framer-motion";
import { DollarSign, Clock, TrendingUp, Shield, Check, X } from "lucide-react";
import { Badge } from "../ui/badge";

const BENEFITS = [
  {
    icon: DollarSign,
    title: "Save $50,000+/year",
    description: "vs. traditional marketing agencies",
    color: "bg-green-500/20",
    iconColor: "#22c55e"
  },
  {
    icon: Clock,
    title: "85% Time Saved",
    description: "Automate repetitive marketing tasks",
    color: "bg-primary/20",
    iconColor: "#00e5ff"
  },
  {
    icon: TrendingUp,
    title: "3x More Content",
    description: "Produce more without hiring",
    color: "bg-purple-500/20",
    iconColor: "#a855f7"
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "SOC 2 Type II compliant",
    color: "bg-blue-500/20",
    iconColor: "#3b82f6"
  }
];

const COMPARISON = [
  { feature: "Campaign Launch Time", yasno: "5 minutes", traditional: "2-4 weeks" },
  { feature: "Monthly Content Pieces", yasno: "500+", traditional: "20-50" },
  { feature: "Languages Supported", yasno: "12+", traditional: "1-2" },
  { feature: "Monthly Cost", yasno: "$499", traditional: "$5,000+" },
  { feature: "24/7 Availability", yasno: true, traditional: false }
];

/**
 * WhyYASNOSection Component - NO ITALICS
 */
export default function WhyYASNOSection() {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-12"
        >
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Why YASNO
          </Badge>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* Left - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tighter mb-4">
              The Marketing Team <span className="gradient-text">You've Always Wanted</span>
            </h2>
            <p className="text-muted-foreground mb-6 sm:mb-8 text-base sm:text-lg">
              Stop spending thousands on agencies or hours on content creation. YASNO delivers 
              enterprise-grade marketing automation at a fraction of the cost.
            </p>

            <div className="space-y-3 sm:space-y-4">
              {BENEFITS.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/10 hover:border-primary/30 transition-all card-hover"
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${benefit.color}`}>
                    <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: benefit.iconColor }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm sm:text-base">{benefit.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Comparison Table */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-4 sm:p-6 border border-white/10"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 tracking-tight">YASNO vs. Traditional</h3>
            
            <div className="space-y-3 sm:space-y-4">
              {COMPARISON.map((row, index) => (
                <div 
                  key={row.feature}
                  className={`grid grid-cols-3 gap-2 sm:gap-4 py-2 sm:py-3 text-xs sm:text-sm ${index !== COMPARISON.length - 1 ? 'border-b border-white/10' : ''}`}
                >
                  <div className="text-muted-foreground">{row.feature}</div>
                  <div className="text-center font-semibold text-primary">
                    {typeof row.yasno === 'boolean' ? (
                      row.yasno ? <Check className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-primary" /> : <X className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-red-500" />
                    ) : row.yasno}
                  </div>
                  <div className="text-center text-muted-foreground">
                    {typeof row.traditional === 'boolean' ? (
                      row.traditional ? <Check className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-green-500" /> : <X className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-muted-foreground" />
                    ) : row.traditional}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
